import React, { HTMLProps, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import CurrencyInput from 'react-currency-input-field';
import classNames from 'classnames';
import CopyIcon from '../../../assets/icons/copy';
import { PASSWORD_INPUT } from '../../../constants';
import { useAppDispatch } from '../../../store';
import { setPush } from '../../../store/actions/app';
import IconBtn from '../../icon-btn/icon-btn';
import PasswordIcon from './input-password-icon';

import './ui-input.scss';

type UiInputProps = HTMLProps<HTMLInputElement> & {
  leftIcon?: string;
  rightIcon?: string;
  label?: string;
  sm?: boolean;
  lg?: boolean;
  numeric?: boolean;
  error?: string | boolean;
  rightText?: string;
  rightTextClassName?: string;
  mask?: string;
  copy?: boolean;
  className?: string;
  currency?: boolean;
  integerCurrency?: boolean;
  onMaskValueChange?: (value: string) => void;
  onInputBlur?: (e: React.FocusEvent<any>) => void;
  onEnterPress?: () => void;
};

const UiInput: React.FC<UiInputProps> = ({ type = 'text', ...props }) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<NumberFormat<any>>(null);
  const [active, setActive] = useState(false);
  const [isPasswordVisible, setPasswordVisibility] = useState(false);

  const passwordInputType = isPasswordVisible ? 'text' : PASSWORD_INPUT;

  const {
    lg,
    sm,
    numeric,
    leftIcon,
    rightIcon,
    label,
    error,
    rightText,
    rightTextClassName,
    mask,
    onMaskValueChange,
    onInputBlur,
    onEnterPress,
    currency,
    integerCurrency,
    copy,
    ...omit
  } = props;

  const handleCopy = () => {
    props.value &&
      navigator.clipboard.writeText(String(props.value)).then(() => {
        document.execCommand('copy');
        dispatch(setPush({ message: 'Данные скопированны', type: 'success' }));
      });
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={classNames('input', props.className && props.className, {
        'input--completed': inputRef.current?.value,
        'input--sm': sm,
        'input--lg': lg,
        'input--active': active,
        'input--disabled': props.disabled,
        'input--readOnly': props.readOnly,
        'input--error': error,
        'input--password': type === PASSWORD_INPUT,
      })}
    >
      {leftIcon && <IconBtn className="input__left" icon={leftIcon} />}
      {currency ? (
        <CurrencyInput
          placeholder={props.placeholder}
          autoComplete="new-password"
          ref={inputRef}
          decimalsLimit={integerCurrency ? 0 : 2}
          allowNegativeValue={false}
          groupSeparator=" "
          decimalSeparator='.'
          disabled={props.disabled}
          value={props.value ? props.value.toString() : undefined}
          onValueChange={(value: any) =>
            props.onChange && props.onChange(value)
          }
          onFocus={() => setActive(true)}
          onBlur={(e: any) => {
            e.persist();
            e.stopPropagation();
            setActive(false);
            if (onInputBlur) onInputBlur(e);
          }}
          onKeyPress={(e: any) =>
            e.code === 'Enter' && onEnterPress && onEnterPress()
          }
          type={type === PASSWORD_INPUT ? passwordInputType : type}
          className="input__input"
        />
      ) : !mask ? (
        <input
          {...omit}
          autoComplete="new-password"
          ref={inputRef}
          disabled={props.disabled}
          value={props.value ? props.value.toString() : undefined}
          onChange={(e) => props.onChange && props.onChange(e)}
          onFocus={() => setActive(true)}
          onBlur={(e) => {
            e.persist();
            e.stopPropagation();
            setActive(false);
            if (onInputBlur) onInputBlur(e);
          }}
          onKeyPress={(e) =>
            e.code === 'Enter' && onEnterPress && onEnterPress()
          }
          type={type === PASSWORD_INPUT ? passwordInputType : type}
          className="input__input"
        />
      ) : (
        <NumberFormat
          placeholder={props.placeholder}
          ref={numberRef}
          isNumericString
          autoComplete="new-password"
          onValueChange={(values) =>
            onMaskValueChange && onMaskValueChange(values.value)
          }
          onChange={props.onChange}
          name={props.name}
          format={mask}
          className="input__input"
          required={props.required}
        />
      )}
      {props.label && !props.placeholder && (
        <span className="input__label">{props.label}</span>
      )}
      {rightText && (
        <span
          className={classNames(
            'input__text input__text--right',
            rightTextClassName ? { [rightTextClassName]: true } : {}
          )}
        >
          {rightText}
        </span>
      )}
      {rightIcon && type !== PASSWORD_INPUT && (
        <IconBtn className="input__left" icon={rightIcon} />
      )}
      {copy && props.value && (
        <IconBtn
          className="input__left"
          icon={<CopyIcon />}
          onClick={handleCopy}
        />
      )}
      {type === PASSWORD_INPUT && (
        <button
          className="input__show-button"
          onClick={() => setPasswordVisibility(!isPasswordVisible)}
          type="button"
        >
          <PasswordIcon isOpen={isPasswordVisible} />
        </button>
      )}
    </div>
  );
};

export default UiInput;
